import type { TowerDesign } from "@/game/towerSpec";

/**
 * Guyed mast: slim triangular lattice held up by four guy rings (à la ROHN 45G).
 *
 * Why it stands (see docs/TOWER_SPEC.md §7, docs/ENGINEERING.md §2):
 *   - Short unguyed base segment. The lowest guy sits at 0.16·H → level 4 →
 *     y = 10 m. The base→first-guy stub is the classic buckling danger zone; at
 *     10 m of a 0.7 m-radius triangle it is stocky enough (built-up column
 *     Pcr ≫ MN) to stay put instead of folding as a mechanism.
 *   - Four guy rings cut the unsupported buckling length to 10 / 15 / 12.5 /
 *     12.5 m spans — each short relative to the ~1.4 m base width, so no span is
 *     a slender pin-pin column.
 *   - Wide-ish, stiff base triangle (baseRadius 0.7 m → 1.4 m across, ROHN 45G
 *     class) gives the fixed base a real rotational couple, so the mast can't
 *     pivot off the foundation.
 *   - Short 2.5 m panels with full X-bracing → every leg segment is a stocky
 *     column (Pcr ≈ 400 kN, yield-governed) that shrugs off the pretension.
 *   - Moderate pretension: 3/8" EHS at strain 0.0010 ≈ 7.9 kN ≈ 12% of the 68 kN
 *     breaking load — in the standard 8–15% band, so the mast is snug but not
 *     preloaded into a buckling-margin deficit. Total accumulated vertical
 *     compression from all four rings is only ~45 kN, a fraction of leg capacity,
 *     so peak member utilisation at zero wind is comfortably low.
 *   - Anchor radius 45–50 m ≈ 0.75–0.83·H (the standard ~0.8×), giving guy
 *     angles of ~55–65° from vertical: steep enough for good lateral stiffness.
 *
 * It stands at zero wind and fails plausibly as wind rises: the windward guys
 * lose tension / a leeward guy overloads, then the mast buckles between rings.
 *
 * Owned by the guyed agent. See docs/ENGINEERING.md §2.
 */
export const design: TowerDesign = {
  id: "guyed-60m",
  name: "Guyed Mast",
  description: "60 m triangular guyed mast, four guy levels (ROHN 45G class).",
  spec: {
    type: "guyed",
    height: 60,
    stack: {
      faces: 3,
      panels: 24, // 2.5 m panels — short, stocky leg segments
      baseRadius: 0.7, // 1.4 m across → stiff base couple (ROHN 45G class)
      topRadius: 0.55,
      legSection: "legHeavy", // Pipe 4" — Pcr ≈ 660 kN over a 2.5 m panel
      braceSection: "braceMed", // L3x3 diagonals
      bracing: "X",
    },
    guys: [
      // lowest guy LOW → 10 m unguyed base stub (short, stocky, no mechanism)
      { atHeightFrac: 0.16, anchorRadius: 45, section: "guy38", pretensionStrain: 0.001 },
      { atHeightFrac: 0.4, anchorRadius: 48, section: "guy38", pretensionStrain: 0.001 },
      { atHeightFrac: 0.62, anchorRadius: 50, section: "guy38", pretensionStrain: 0.001 },
      { atHeightFrac: 0.85, anchorRadius: 50, section: "guy38", pretensionStrain: 0.001 },
    ],
  },
};
